// F1: Mirror — full-screen session privacy receipt

import { useState } from 'react';
import { ANALYZING_COPY, CategoryChip, Icons, InferenceFreshness } from './ui-atoms.jsx';
import { Confidence } from './extensions.jsx';
import { MeterPanel } from './meter-panel.jsx';
import { useCopy } from './representation.jsx';
export function MirrorScreen({
  mirror,
  meter,
  onClose,
  onJumpToMessage,
  onOpenDrilldown,
  onCategorySelect,
  onEvidenceJump,
  unavailable = false,
  mirrorPending = false,
  meterPending = false,
  meterUnavailable = false,
  inferenceTriggerTime = null,
}) {
  const categories = mirror.categories.length
    ? mirror.categories
    : [
        mirrorPending
          ? {
              key: 'Analyzing',
              confidence: 0,
              summary: ANALYZING_COPY,
              evidence: [],
            }
          : {
              key: 'No findings',
              confidence: 0,
              summary: 'No session-level findings are available yet.',
              evidence: [],
            },
      ];
  const [selected, setSelected] = useState(categories[0].key);
  const cat = categories.find((c) => c.key === selected) ?? categories[0];
  const disclaimer = useCopy('mirror_disclaimer');

  return (
    <div className="fullsheet">
      {/* Header */}
      <div
        style={{
          padding: '54px 20px 12px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--paper)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close Mirror dashboard"
            style={{
              background: 'var(--paper-2)',
              border: 'none',
              borderRadius: 999,
              width: 34,
              height: 34,
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            {Icons.back(16, 'var(--ink)')}
          </button>
          <div className="eyebrow">Mirror · F1</div>
          <div style={{ width: 34 }} />
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.2 }}>
          Privacy receipt
        </div>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--ink-muted)',
            marginTop: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>{mirror.sessionLength}</span>
          {inferenceTriggerTime && (
            <>
              <span>·</span>
              <InferenceFreshness triggerTime={inferenceTriggerTime} />
            </>
          )}
        </div>
      </div>

      <div className="scroll" style={{ flex: 1, overflow: 'auto' }}>
        {unavailable && (
          <div
            role="status"
            style={{ margin: '12px 16px 0', color: 'var(--ink-soft)', fontSize: 12 }}
          >
            Mirror analysis is unavailable right now. Your sent messages were not affected.
          </div>
        )}
        {/* Cumulative exposure — same MeterPanel as F3 RiskMeterSheet,
            rendered with oneSided so only the participant's own polygon
            and legend chip appear. The per-axis breakdown is F3-only and
            is suppressed here. Chrome differs (fullsheet here vs bottom
            sheet in F3). */}
        <div style={{ padding: '16px 16px 0' }}>
          {meterPending ? (
            <div role="status" style={{ color: 'var(--ink-soft)', fontSize: 12 }}>
              {ANALYZING_COPY}
            </div>
          ) : meterUnavailable ? (
            <div role="status" style={{ color: 'var(--ink-soft)', fontSize: 12 }}>
              Risk analysis is unavailable right now.
            </div>
          ) : (
            <MeterPanel meter={meter} oneSided onOpenDrilldown={onOpenDrilldown} size={300} />
          )}
        </div>

        {/* Categories */}
        <div style={{ padding: '16px 16px 8px' }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            Inferred about you
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              overflowX: 'auto',
              paddingBottom: 4,
              scrollbarWidth: 'none',
            }}
          >
            {categories.map((c) => (
              <CategoryChip
                key={c.key}
                label={c.key}
                active={selected === c.key}
                onClick={() => {
                  setSelected(c.key);
                  onCategorySelect?.(c.key);
                }}
              />
            ))}
          </div>
        </div>

        {/* Active category detail */}
        <div style={{ padding: '4px 16px 16px' }}>
          <div
            style={{
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 17, fontWeight: 600 }}>{cat.key}</span>
                <Confidence value={cat.confidence} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
                {cat.summary}
              </div>
            </div>
            <div style={{ padding: '12px 16px 14px' }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                Evidence · {cat.evidence.length} snippet{cat.evidence.length > 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cat.evidence.map((e, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      onEvidenceJump?.(cat.key, e.msgId);
                      onJumpToMessage?.(e.msgId);
                    }}
                    style={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: 'var(--paper-2)',
                      border: '1px solid var(--line)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      fontFamily: 'inherit',
                      transition: 'background 120ms ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-3)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--paper-2)')}
                  >
                    <span style={{ fontSize: 13, lineHeight: 1.45, flex: 1 }}>
                      &quot;{e.snippet}&quot;
                    </span>
                    <span style={{ color: 'var(--ink-muted)', marginTop: 4 }}>
                      {Icons.chevron(12, 'currentColor')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div
          style={{
            margin: '4px 16px 24px',
            padding: 14,
            background: 'var(--indigo-soft)',
            borderRadius: 12,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ marginTop: 2, flexShrink: 0 }}>{Icons.info(14, 'var(--indigo)')}</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--indigo)' }}>{disclaimer}</div>
        </div>
      </div>
    </div>
  );
}
